// Constants
import { platforms } from '../../constants'

/**
 * The 'connection' namespace is used to connect and maintain connections between
 * the SDK and one or more backend servers.
 *
 * @public
 * @namespace connection
 */

/**
 * Information about a websocket connection.
 *
 * Can be retrieved using the {@link connection.getSocketState} API.
 *
 * @public
 * @static
 * @typedef {Object} WSConnectionObject
 * @memberof connection
 * @property {boolean} connected The state of the websocket connection.
 * @property {boolean} pinging True if the client has sent a ping to the server and is still waiting for a pong response.
 * @property {Object} method Information about how the websocket is being used.
 * @property {string} [method.type] How the websocket is staying connected.
 * @property {string} [method.responsibleParty] Who is responsible for keeping the connection alive.
 * @property {string} platform The SDK platform being used.
 * @property {number} pingInterval How often the client will ping the server to test for websocket connectivity.
 * @property {number} reconnectLimit How many times the SDK will try to reconnect a disconnected websocket.
 * @property {number} reconnectDelay How long the SDK will wait before retrying websocket reconnection.
 * @property {number} reconnectTimeMultiplier Reconnect delay multiplier for subsequent attempts. The reconnect delay time will be multiplied by this after each failed reconnect attempt to increase the delay between attempts. eg. 5000ms then 10000ms then 20000ms delay if value is 2.
 * @property {number} reconnectTimeLimit Maximum time delay between reconnect attempts (milliseconds). Used in conjunction with `reconnectTimeMultiplier` to prevent overly long delays between reconnection attempts.
 * @property {boolean} autoReconnect Indicates if the SDK should automatically try reconnecting a disconnected websocket.
 * @property {number} maxMissedPings How many missed pings before the SDK stops trying to reconnect a disconnected websocket.
 * @property {string} webSocketOAuthMode The mode used for authenticating with the server.
 * @property {Object} wsInfo Information required to connect a websocket to the server.
 * @property {string} [wsInfo.protocol] The protocol to use to connect a websocket.
 * @property {string} [wsInfo.server] The domain name or IP address of the server to connect to.
 * @property {number} [wsInfo.port] The port of the server to connect to.
 * @property {string} [wsInfo.url] The URL path to use to request a websocket connection.
 * @property {string} [wsInfo.params] Any additional params that might be required by the server to establish the websocket connection.
 * @property {number} lastContact The date and time that the last known contact with the server was.
 */

import { changeConnectivityChecking, lostConnection } from './actions'

// Selectors
import { getConnectionState } from './selectors'
import { getPlatform } from '../../auth/interface/selectors'

import { logManager, API_LOG_TAG } from '../../logs'

const log = logManager.getLogger('CONNECTIVITY')

export default function api ({ dispatch, getState }) {
  const connectivityApi = {
    /**
     * Get the state of the websocket.
     * @public
     * @static
     * @memberof connection
     * @method getSocketState
     * @param {string} [platform='link'] Backend platform for which to request the websocket's state.
     * @return {connection.WSConnectionObject} Details about the current websocket connection, including state and configuration.
     */
    getSocketState (platform = platforms.LINK) {
      log.debug(API_LOG_TAG + 'connection.getSocketState: ', platform)
      return getConnectionState(getState(), platform)
    },

    /**
     * Enables or disables connectivity checking.
     * @public
     * @memberof connection
     * @method enableConnectivityChecking
     * @param {boolean} enable Enable connectivity checking.
     */
    enableConnectivityChecking (enable) {
      log.debug(API_LOG_TAG + 'connection.enableConnectivityChecking: ', enable)
      dispatch(changeConnectivityChecking(enable))
    },

    /**
     * Triggers a reset in the connection to the WebSocket being used for notifications.
     * This can be used in scenarios where a network issue (undetectable by the SDK)
     * is detected by an application.
     *
     * If there is no WebSocket currently connected, this function has no effect.
     * Calling this function will trigger all the normal WebSocket and connectivity lifecycle
     * events as well as trigger re-connection processing that follows the configuration of the SDK.
     * Calling this function always has the potential of causing some events being lost by the
     * SDK and preventing proper operation.
     * @public
     * @memberof connection
     * @method resetConnection
     */
    resetConnection () {
      const state = getState()
      const platform = getPlatform(state)
      log.debug(API_LOG_TAG + 'connection.resetConnection: ', platform)

      const wsState = getConnectionState(state, platform)
      if (!wsState.connected) {
        log.debug(API_LOG_TAG + 'connection.resetConnection: No active websocket connection. Ignoring request.')
        return
      }
      // Dispatching this action would close/cleanup existing ws connection
      // However, we'll re-connect after that.
      dispatch(lostConnection(undefined, platform))
    }
  }
  return { connection: connectivityApi }
}
